/**
 * bmm-collection-filter.js  v1.0
 * Clothio — Collection Filter Bar
 *
 * Handles:
 * 1. Filter dropdown open/close (click + keyboard)
 * 2. Sort dropdown open/close
 * 3. Close dropdowns on outside click / Escape key
 * 4. Active count badge updates after AJAX filter
 * 5. Theme editor re-init
 *
 * Does NOT handle:
 * - Filter form submission (Ella's collection-filters-form.js owns this)
 * - URL history (Ella's collection-filters-form.js owns this)
 * - Grid re-render (Ella's collection-filters-form.js owns this)
 *
 * Vanilla JS. No jQuery. No globals (IIFE).
 */

(function () {
  'use strict';

  // ── SELECTORS ──────────────────────────────────────────────
  var SEL = {
    tabWrap:    '.bmm-cf__tab-wrap',
    tab:        '.bmm-cf__tab',
    panel:      '.bmm-cf__panel',
    closeBtn:   '.js-cf-close',
    sortWrap:   '.bmm-cf__sort-wrap',
    sortSelect: '.bmm-cf__sort-select',
    sortMenu:   '.bmm-cf__sort-menu',
    pills:      '.bmm-cf__pills',
    mobileSort: '.bmm-cf__mobile-sort',
    countEl:    '.bmm-cf__count .count',
  };

  // ── INIT ───────────────────────────────────────────────────
  function init(root) {
    if (!root) return;

    initFilterTabs(root);
    initSortDropdown(root);
    initMobileSort(root);
    initOutsideClick(root);
    initEscapeKey(root);
    observeAjaxUpdates(root);
  }

  // ── FILTER TABS ────────────────────────────────────────────
  function initFilterTabs(root) {
    var tabWraps = root.querySelectorAll(SEL.tabWrap);

    tabWraps.forEach(function (wrap) {
      var btn   = wrap.querySelector(SEL.tab);
      var panel = wrap.querySelector(SEL.panel);
      if (!btn || !panel) return;

      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = wrap.classList.contains('is-open');
        // Close all
        closeAllTabs(root);
        // Toggle this
        if (!isOpen) openTab(wrap, btn, panel);
      });

      // Keyboard: Enter/Space open, Escape close
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });

      // "Show Results" / close button inside panel
      var closeBtns = panel.querySelectorAll(SEL.closeBtn);
      closeBtns.forEach(function (cb) {
        cb.addEventListener('click', function () {
          closeTab(wrap, btn, panel);
          btn.focus();
        });
      });
    });
  }

  function openTab(wrap, btn, panel) {
    wrap.classList.add('is-open');
    btn.setAttribute('aria-expanded', 'true');
    panel.removeAttribute('hidden');
    // Focus first interactive element
    var first = panel.querySelector('input, button, a, [tabindex]');
    if (first) setTimeout(function () { first.focus(); }, 50);
  }

  function closeTab(wrap, btn, panel) {
    wrap.classList.remove('is-open');
    btn.setAttribute('aria-expanded', 'false');
    panel.setAttribute('hidden', '');
  }

  function closeAllTabs(root) {
    root.querySelectorAll(SEL.tabWrap + '.is-open').forEach(function (wrap) {
      var btn   = wrap.querySelector(SEL.tab);
      var panel = wrap.querySelector(SEL.panel);
      if (btn && panel) closeTab(wrap, btn, panel);
    });
  }

  // ── SORT DROPDOWN ──────────────────────────────────────────
  function initSortDropdown(root) {
    var sortWrap   = root.querySelector(SEL.sortWrap);
    var sortSelect = root.querySelector(SEL.sortSelect);
    var sortMenu   = root.querySelector(SEL.sortMenu);
    if (!sortWrap || !sortSelect || !sortMenu) return;

    sortSelect.addEventListener('click', function (e) {
      e.stopPropagation();
      var isOpen = sortSelect.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeSortMenu(sortSelect, sortMenu);
      } else {
        closeAllTabs(root); // close any open filter panels
        openSortMenu(sortSelect, sortMenu);
      }
    });

    sortSelect.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        sortSelect.click();
      }
    });

    // Sort item click — update URL + re-render grid via Ella's AJAX engine
    sortMenu.querySelectorAll('[data-sort-by-item]').forEach(function (item) {
      item.addEventListener('click', function (e) {
        e.preventDefault();
        var sortValue = this.querySelector('.text') && this.querySelector('.text').dataset.value;
        if (!sortValue) sortValue = this.querySelector('.text') && this.querySelector('.text').dataset.href;
        if (!sortValue) return;

        // Update active state in menu
        sortMenu.querySelectorAll('li').forEach(function (li) { li.classList.remove('is-active'); });
        item.classList.add('is-active');

        // Update sort trigger label
        var currentLabel = sortSelect.querySelector('.bmm-cf__sort-current');
        if (currentLabel) currentLabel.textContent = item.querySelector('.text').textContent.trim();

        // Close menu
        closeSortMenu(sortSelect, sortMenu);

        // Build URL with new sort_by param and trigger Ella's AJAX engine
        var form = root.querySelector('#CollectionFiltersForm');
        if (!form) return;

        // Add/update sort_by hidden input
        var existing = form.querySelector('input[name="sort_by"]');
        if (existing) {
          existing.value = sortValue;
        } else {
          var input = document.createElement('input');
          input.type  = 'hidden';
          input.name  = 'sort_by';
          input.value = sortValue;
          form.appendChild(input);
        }

        // Dispatch input event — Ella's debouncedOnSubmit listens for 'input' on the form
        form.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
  }

  function openSortMenu(trigger, menu) {
    trigger.setAttribute('aria-expanded', 'true');
    menu.style.display = 'block';
  }

  function closeSortMenu(trigger, menu) {
    trigger.setAttribute('aria-expanded', 'false');
    menu.style.display = 'none';
  }

  // ── MOBILE SORT ────────────────────────────────────────────
  // Mobile sort is a native <select>. On change, submit the filter
  // form with the new sort_by value so Ella's AJAX handles it.
  function initMobileSort(root) {
    var mobileSort = root.querySelector(SEL.mobileSort);
    if (!mobileSort) return;
    mobileSort.addEventListener('change', function () {
      var val = this.value;
      // Find the form and add/update sort_by input
      var form = root.querySelector('#CollectionFiltersForm');
      if (!form) return;
      var existing = form.querySelector('input[name="sort_by"]');
      if (existing) {
        existing.value = val;
      } else {
        var input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = 'sort_by';
        input.value = val;
        form.appendChild(input);
      }
      // Trigger Ella's AJAX filter engine
      form.dispatchEvent(new Event('submit', { bubbles: true }));
    });
  }

  // ── OUTSIDE CLICK ──────────────────────────────────────────
  function initOutsideClick(root) {
    document.addEventListener('click', function (e) {
      // If click is outside the filter bar, close everything
      if (!root.contains(e.target)) {
        closeAllTabs(root);
        var sortSelect = root.querySelector(SEL.sortSelect);
        var sortMenu   = root.querySelector(SEL.sortMenu);
        if (sortSelect && sortMenu) closeSortMenu(sortSelect, sortMenu);
      }
    });
  }

  // ── ESCAPE KEY ─────────────────────────────────────────────
  function initEscapeKey(root) {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      // Close any open filter tabs
      var openWraps = root.querySelectorAll(SEL.tabWrap + '.is-open');
      if (openWraps.length > 0) {
        openWraps.forEach(function (wrap) {
          var btn   = wrap.querySelector(SEL.tab);
          var panel = wrap.querySelector(SEL.panel);
          if (btn && panel) {
            closeTab(wrap, btn, panel);
            btn.focus(); // return focus to trigger
          }
        });
        return;
      }
      // Close sort dropdown
      var sortSelect = root.querySelector(SEL.sortSelect);
      var sortMenu   = root.querySelector(SEL.sortMenu);
      if (sortSelect && sortMenu && sortSelect.getAttribute('aria-expanded') === 'true') {
        closeSortMenu(sortSelect, sortMenu);
        sortSelect.focus();
      }
    });
  }

  // ── AJAX UPDATE OBSERVER ───────────────────────────────────
  // After Ella's AJAX re-renders the filter form (#CollectionFiltersForm),
  // we need to re-sync our active count badges and pill strip.
  function observeAjaxUpdates(root) {
    // Ella dispatches a custom event after AJAX completes
    document.addEventListener('collection:filtered', function () {
      syncActiveCounts(root);
    });

    // Also observe DOM mutations on the pills strip
    // (Ella re-renders the pills area via innerHTML swap)
    var pillsEl = root.querySelector(SEL.pills);
    if (!pillsEl) return;

    var observer = new MutationObserver(function () {
      syncActiveCounts(root);
    });
    observer.observe(root, { childList: true, subtree: true, attributes: false });
  }

  function syncActiveCounts(root) {
    // Count active filter values from checked inputs
    var totalActive = 0;
    root.querySelectorAll('#CollectionFiltersForm input[type="checkbox"]:checked').forEach(function () {
      totalActive++;
    });
    // Count price range inputs with values
    var priceMin = root.querySelector('#CollectionFiltersForm input[name*="filter.p.price.gte"]');
    var priceMax = root.querySelector('#CollectionFiltersForm input[name*="filter.p.price.lte"]');
    if ((priceMin && priceMin.value) || (priceMax && priceMax.value)) totalActive++;

    // Update pills strip visibility
    var pills = root.querySelector(SEL.pills);
    if (pills) {
      if (totalActive > 0) pills.classList.remove('is-hidden');
      else pills.classList.add('is-hidden');
    }

    // Update mobile filter button active state
    var mobileBtn = root.querySelector('.bmm-cf__mobile-filter');
    if (mobileBtn) {
      if (totalActive > 0) mobileBtn.classList.add('has-active');
      else mobileBtn.classList.remove('has-active');
    }

    // Update mobile count badge
    var mobileCount = root.querySelector('.bmm-cf__mobile-count');
    if (mobileCount) {
      if (totalActive > 0) {
        mobileCount.textContent = totalActive;
        mobileCount.style.display = '';
      } else {
        mobileCount.style.display = 'none';
      }
    }
  }

  // ── BOOT ───────────────────────────────────────────────────
  function boot() {
    document.querySelectorAll('.bmm-cf').forEach(function (root) {
      init(root);
    });
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init on section load
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target.querySelector('.bmm-cf');
      if (root) init(root);
    });
  }

})();
